import { ArrowDown, ArrowUp } from '@atom-learning/icons'
import type { Header } from '@tanstack/react-table'
import { flexRender } from '@tanstack/react-table'
import * as React from 'react'
import { useHover } from 'react-aria'

import { styled } from '~/styled'
import { cn } from '~/styled'

import { Icon } from '../icon/Icon'
import { Table } from '../table/Table'
import { Text } from '../text/Text'
import { useDataTable } from './DataTableContext'

type DataTableHeaderProps = React.ComponentProps<typeof Table.HeaderCell> & {
  header: Header<Record<string, unknown>, unknown>
  includeTotal?: boolean
  totalSuffix?: string
}

const StyledCount = styled(Text, {
  base: ['text-grey-600', 'whitespace-nowrap', 'hidden', 'min-[800px]:block']
})

const StyledTableHeaderCell = styled(Table.HeaderCell, {
  base: [],
  variants: {
    isSortable: {
      true: [
        '*:relative',
        'before:absolute',
        'before:bg-primary-100',
        'before:duration-200',
        'before:ease-in',
        'before:inset-0',
        'before:opacity-0',
        'before:transition-opacity',
        'cursor-pointer',
        'hover:before:opacity-50',
        'relative'
      ]
    }
  }
})

const sortIcons = {
  asc: ArrowUp,
  desc: ArrowDown
}

const SortIcon = ({ direction }: { direction: 'asc' | 'desc' }) => (
  <Icon
    is={sortIcons[direction]}
    size="sm"
    className="stroke-primary-900 absolute left-1"
  />
)

export const DataTableHeaderCell = ({
  header,
  includeTotal = false,
  totalSuffix = 'items',
  children,
  className,
  ...props
}: DataTableHeaderProps) => {
  const sortDirection = header.column.getIsSorted()
  const { isSortable: isSortableTable, getRowModel } = useDataTable()
  // false for display columns, e.g. "Actions"
  const isDataColumn = header.column.getCanSort()

  const totalRows = getRowModel()?.rows?.length

  const { hoverProps, isHovered } = useHover({})

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLTableCellElement>,
    callback: (event: unknown) => void | undefined
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      callback(event as unknown)
    }
  }

  const headerSortingHandler = header.column.getToggleSortingHandler()

  return (
    <StyledTableHeaderCell
      isSortable={isSortableTable && isDataColumn}
      colSpan={header.colSpan}
      onClick={
        isSortableTable && isDataColumn && headerSortingHandler
          ? headerSortingHandler
          : undefined
      }
      onKeyDown={(event) =>
        isSortableTable &&
        isDataColumn &&
        headerSortingHandler &&
        handleKeyDown(event, headerSortingHandler)
      }
      tabIndex={isSortableTable && isDataColumn ? 0 : -1}
      {...hoverProps}
      className={className}
      {...props}
    >
      <div
        className={cn(
          'flex',
          'gap-1',
          'items-center',
          header.colSpan > 1 ? 'justify-center' : 'justify-start'
        )}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
        {sortDirection && isSortableTable && (
          <div
            className={cn(
              'flex',
              'items-center',
              'relative',
              'size-6',
              'ml-2',
              isHovered ? 'bg-primary-200' : 'bg-primary-100',
              'rounded-sm'
            )}
          >
            <SortIcon direction={sortDirection} />
          </div>
        )}
        {includeTotal && (
          <StyledCount size="sm" weight="normal">
            ({totalRows} {totalSuffix})
          </StyledCount>
        )}
      </div>
    </StyledTableHeaderCell>
  )
}
